import { createShallowStore, createStoreWithSubscribe } from '@frontend/store';

type SelectedThreads = Record<string, string[]>;

interface InboxSelectionStoreState {
  selectedThreads: SelectedThreads;
  isBulkSelect: boolean;
  setSelectedThreads: (selectedThreads: SelectedThreads) => void;
  toggleThreadSelection: (args: { threadId: string; locationId: string }) => void;
  toggleBulkSelect: () => void;
  resetSelection: () => void;
}

/**
 * Global store for inbox bulk-selection state.
 *
 * Holding selection here (rather than in component state) lets each list row
 * subscribe to only its own selected flag via {@link useIsThreadSelected}.
 * Toggling a single thread therefore re-renders just that row instead of
 * recreating the list renderer and re-rendering every visible item.
 */
export const useInboxSelectionStore = createStoreWithSubscribe<InboxSelectionStoreState>(
  (set, get) => ({
    selectedThreads: {},
    isBulkSelect: false,

    setSelectedThreads: (selectedThreads) => set({ selectedThreads }, false, 'SET_SELECTED_THREADS'),

    toggleThreadSelection: ({ threadId, locationId }) => {
      const prev = get().selectedThreads;
      const currentLocationEntries = prev[locationId] ?? [];
      const isCurrentlySelected = currentLocationEntries.includes(threadId);
      const newEntries = isCurrentlySelected
        ? currentLocationEntries.filter((id) => id !== threadId)
        : [...currentLocationEntries, threadId];
      set({ selectedThreads: { ...prev, [locationId]: newEntries } }, false, 'TOGGLE_THREAD_SELECTION');
    },

    toggleBulkSelect: () => {
      const { isBulkSelect, selectedThreads } = get();
      if (isBulkSelect && Object.keys(selectedThreads).length) {
        set({ isBulkSelect: false, selectedThreads: {} }, false, 'TOGGLE_BULK_SELECT');
      } else {
        set({ isBulkSelect: !isBulkSelect }, false, 'TOGGLE_BULK_SELECT');
      }
    },

    resetSelection: () => set({ isBulkSelect: false, selectedThreads: {} }, false, 'RESET_SELECTION'),
  }),
  { name: 'InboxSelectionStore', trace: true }
);

export const useInboxSelectionShallowStore = createShallowStore(useInboxSelectionStore);

/**
 * Subscribes a single row to its own selected state. Only re-renders the
 * calling component when this specific thread's selection changes.
 */
export const useIsThreadSelected = (locationId: string, threadId: string): boolean =>
  useInboxSelectionStore((state) => !!state.selectedThreads[locationId]?.includes(threadId));
